import React, { useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../store";
import ExperienceDisplay from "./ExperienceDisplay";
import { User, UserExperince } from "../Profile";
import { MonthPicker } from "../MonthPicker";
import { Checkbox } from "@mui/material";
import { toast } from "react-toastify";

type ExperienceProps = {
  handleClick: () => void;
};

export const Experience: React.FC<ExperienceProps> = ({ handleClick }) => {
  const dispatch = useDispatch();

  const [role, setRole] = useState("");
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [description, setDescription] = useState("");
  const [failedSubmit, setFailedSubmit] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = React.useState(false);
  const [isCurrentRole, setIsCurrentRole] = React.useState(false);

  const user = useSelector((state: AppState) => state.user); // get the current user object from the Redux store
  const [experiences, setExperiences] = useState<UserExperince[]>(
    user.experiences == undefined ? [] : user.experiences
  );
  const [numExperiences, setNumExperiences] = useState(
    user.experiences == undefined ? 0 : user.experiences.length
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const updateState = (exp: UserExperince[]) => {
    const updatedUser: User = {
      ...user, // create a new object with all existing properties of the user object
      experiences: exp,
    };
    dispatch({
      type: "UPDATE_STATE",
      payload: updatedUser,
    });
  };

  const updateUserData = () => {
    if (role.length === 0 || company.length === 0 || description.length === 0) {
      toast.error("Please complete all required fields.");
      setFailedSubmit(true);
      return;
    }

    setFailedSubmit(false);

    const exp: UserExperince = {
      role: role,
      company: company,
      location: location,
      start_time: startTime,
      end_time: endTime,
      description: description,
      id: numExperiences,
    };

    setNumExperiences(numExperiences + 1);

    const exps = experiences.concat(exp);

    setExperiences(exps);

    updateState(exps);

    setRole("");
    setCompany("");
    setLocation("");
    setDescription("");
  };

  const removeExperience = (index: number) => {
    const exps = experiences.filter((exp) => exp.id != index);
    setExperiences(exps);
    updateState(exps);
  };

  const handleRoleInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    setRole(event.currentTarget.value);
  };

  const handleCompanyInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    setCompany(event.currentTarget.value);
  };

  const handleLocationInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    setLocation(event.currentTarget.value);
  };

  const handleDescription = (event: React.FormEvent<HTMLTextAreaElement>) => {
    setDescription(event.currentTarget.value);
  };

  return (
    <div>
      <p className="text-light mb-10 pl-4 text-2xl font-bold lg:text-3xl xl:text-4xl">
        <span className="text-orange">Work Experience</span>
      </p>

      {isOverlayOpen && (
        <div className="overlay">
          <div className="agreement-content w-1/2">
            <h2 className="text-center font-code md:text-lg lg:text-xl xl:text-2xl ">
              You have a partial work experience entry. Are you sure you want to
              move on?
            </h2>

            <div className="flex w-full flex-row pt-4 lg:pt-6">
              <button
                className="start-button mx-auto px-4 py-2 font-bold md:text-lg lg:text-xl xl:text-2xl"
                onClick={() => handleClick()}
              >
                Yes, I&apos;m sure
              </button>
              <button
                className="login-button mx-auto px-4 py-2 font-bold md:text-lg lg:text-xl xl:text-2xl"
                onClick={() => setIsOverlayOpen(false)}
              >
                Nevermind
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col space-x-10 md:flex-row">
        <div className="ml-auto hidden h-full w-full overflow-y-scroll rounded-md bg-[#1E1E1E] p-2 md:block md:max-h-[40rem] md:w-1/4">
          {experiences.map((exp, index) => {
            const deleteIndex = () => {
              removeExperience(exp.id);
            };

            return (
              <ExperienceDisplay
                key={index}
                role={exp.role}
                company={exp.company}
                start_time={exp.start_time}
                end_time={exp.end_time}
                index={index + 1}
                deleteRow={deleteIndex}
              />
            );
          })}

          {experiences.length === 0 && (
            <h1 className="mt-20 h-32 font-code text-lg font-bold text-[#9CA3AF] lg:text-xl xl:text-2xl">
              No experiences added...
            </h1>
          )}
        </div>

        <div className="w-4/5">
          <div className="flex flex-col md:flex-row">
            <div className="inline-block w-full md:w-1/2">
              <p className="text-orange pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                ROLE <span className="text-red-500">*</span>
              </p>
              <textarea
                value={role}
                onInput={handleRoleInput}
                rows={1}
                className={`answer-box mt-3 max-h-min w-full overflow-y-hidden p-5 text-2xl ${
                  failedSubmit ? "border-2 border-red-600" : ""
                }`}
                ref={textareaRef}
                placeholder="Software Engineering Intern"
              />
            </div>
          </div>
          <div className="flex flex-col md:flex-row">
            <div className="inline-block w-full md:w-1/2">
              <p className="text-blue pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                COMPANY <span className="text-red-500">*</span>
              </p>
              <textarea
                value={company}
                onInput={handleCompanyInput}
                rows={1}
                className={`answer-box mt-3 max-h-min w-full overflow-y-hidden p-5 text-2xl ${
                  failedSubmit ? "border-2 border-red-600" : ""
                }`}
                ref={textareaRef}
                placeholder="ABC Inc."
              />
            </div>
            <div className="ml-auto inline-block w-full md:w-2/5">
              <p className="text-orange pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                LOCATION
              </p>
              <textarea
                value={location}
                rows={1}
                onInput={handleLocationInput}
                className="answer-box mt-3 w-full overflow-y-hidden p-5 text-2xl"
                ref={textareaRef}
                placeholder="The Moon"
              />
            </div>
          </div>
          <div className="flex flex-col md:flex-row">
            <div className="inline-block w-full md:w-1/2">
              <p className="text-orange pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                START DATE
              </p>
              <MonthPicker handleInput={setStartTime} disabled={false} />
            </div>
            <div className="ml-auto inline-block w-full md:w-2/5">
              <p className="text-blue pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                END DATE
              </p>
              <MonthPicker handleInput={setEndTime} disabled={isCurrentRole} />
              <div className="flex flex-row">
                <Checkbox
                  sx={{
                    color: "#F5F5F5",
                  }}
                  checked={isCurrentRole}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setIsCurrentRole(event.target.checked);

                    if (event.target.checked) {
                      setEndTime("Present");
                    }
                  }}
                />
                <p className="text-light mt-2 font-code">
                  This is my current role
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-row">
            <textarea
              value={description}
              rows={5}
              onInput={handleDescription}
              className={`answer-box mt-3 max-h-min w-full overflow-y-hidden p-5 text-2xl ${
                failedSubmit ? "border-2 border-red-600" : ""
              }`}
              ref={textareaRef}
              placeholder="A few sentences about what you did, in plain English. Use numbers if possible."
            />
          </div>

          <div className="flex flex-row space-x-10">
            <button
              className="start-button mt-5 px-10 py-2 text-lg font-bold md:py-4 md:text-xl lg:px-16 lg:text-3xl"
              onClick={updateUserData}
            >
              Add
            </button>
            <button
              className="login-button mt-5 px-10 py-2 text-lg font-bold md:py-4 md:text-xl lg:px-16 lg:text-3xl"
              onClick={() => {
                if (
                  role.length > 0 ||
                  company.length > 0 ||
                  location.length > 0 ||
                  description.length > 0
                ) {
                  setIsOverlayOpen(true);
                } else {
                  handleClick();
                }
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
